import { FunnelSimple } from "@phosphor-icons/react";

import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LinkedInProfile, ProfileSearchParams } from "common/types";

import useSearchSourceSuggestions from "../../../datahooks/useSearchSourceSuggestions";
import useSearchSourceParams from "../../../hooks/useSearchSourceParams";
import SearchAndFiltersEmptyPlaceholder from "../SearchAndFiltersEmptyPlaceholder";
import SearchMobileList from "./SearchMobileList";
import SearchTable from "./SearchTable";

interface PreviewResultsProps {
  searchParams: ProfileSearchParams;
  setSearchParams: (searchParams: Partial<ProfileSearchParams>) => void;
  profiles: LinkedInProfile[];
  isLoading: boolean;
  totalCount: number;
}

export default function PreviewResults({
  searchParams,
  setSearchParams,
  profiles,
  isLoading,
  totalCount,
}: PreviewResultsProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { hasFilters, resetParams } = useSearchSourceParams(
    searchParams,
    setSearchParams,
  );

  const { searchSuggestions, isLoadingSearchSuggestions } =
    useSearchSourceSuggestions();

  if (isLoading || profiles?.length) {
    const Component = isDesktop ? SearchTable : SearchMobileList;

    return (
      <Component
        profiles={profiles}
        isLoading={isLoading}
        totalItemCount={totalCount}
      />
    );
  }
  return (
    <SearchAndFiltersEmptyPlaceholder
      hasFilters={hasFilters}
      onClearAll={resetParams}
      hasSearch={!!searchParams.keywords.length}
      initialActionComponent={
        <div className="flex flex-wrap items-center justify-center gap-2">
          {isLoadingSearchSuggestions || !searchSuggestions
            ? new Array(5).fill({}).map((_, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: no unique key
                <Skeleton key={index} className="h-8 w-44 rounded-full" />
              ))
            : searchSuggestions.map(
                ({ label, keyword_text, ...searchOptions }) => (
                  <Button
                    key={label}
                    variant="outline-black"
                    size="sm"
                    leftIcon={<FunnelSimple />}
                    onClick={() =>
                      setSearchParams({
                        keywords: [
                          {
                            text: keyword_text,
                            type: "general",
                            modifier: "optional",
                          },
                        ],
                        ...searchOptions,
                      })
                    }
                  >
                    {label}
                  </Button>
                ),
              )}
        </div>
      }
    />
  );
}
